import React, { useEffect, useState } from 'react';
import { Ticket, Plus, Loader2, Ban, Trash2 } from 'lucide-react';
import couponsService, { Coupon } from '../../services/couponsApi';

const ui = {
  card: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.07)',
  input: 'rgba(255,255,255,0.06)',
  inputBorder: 'rgba(255,255,255,0.1)',
  label: '#9ca3af',
};

const DISCOUNT_LABELS: Record<Coupon['discount_type'], string> = {
  percent: '% de desconto',
  value: 'R$ de desconto',
  tokens: 'tokens de bônus',
};

const CouponsManager: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<Coupon['discount_type']>('percent');
  const [discountValue, setDiscountValue] = useState<number>(10);
  const [maxRedemptions, setMaxRedemptions] = useState<number>(0);
  const [expiresAt, setExpiresAt] = useState('');

  const load = () => {
    setLoading(true);
    couponsService.list().then(setCoupons).catch(() => setMsg({ type: 'error', text: 'Erro ao carregar cupons.' })).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!code.trim()) {
      setMsg({ type: 'error', text: 'Informe o código do cupom.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await couponsService.create({
        code: code.trim().toUpperCase(),
        description: description || undefined,
        discount_type: discountType,
        discount_value: discountValue,
        max_redemptions: maxRedemptions,
        expires_at: expiresAt ? new Date(expiresAt + 'T23:59:59').toISOString() : null,
      });
      setMsg({ type: 'success', text: 'Cupom criado com sucesso!' });
      setCode(''); setDescription(''); setDiscountValue(10); setMaxRedemptions(0); setExpiresAt('');
      load();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Erro ao criar cupom.' });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: number) => {
    try {
      await couponsService.deactivate(id);
      load();
    } catch {
      setMsg({ type: 'error', text: 'Erro ao desativar cupom.' });
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const remove = async (id: number) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    try {
      await couponsService.remove(id);
      setConfirmDeleteId(null);
      load();
    } catch {
      setMsg({ type: 'error', text: 'Erro ao apagar cupom.' });
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Criar cupom */}
      <div className="rounded-xl p-5 space-y-4" style={{ background: ui.card, border: `1px solid ${ui.border}` }}>
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Ticket size={16} className="text-purple-400" /> Novo Cupom
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: ui.label }}>CÓDIGO</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Ex: BEMVINDO10"
              className="w-full rounded-lg px-3 py-2 text-sm text-white tracking-widest focus:outline-none"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: ui.label }}>DESCRIÇÃO (opcional)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Boas-vindas de indicação"
              className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: ui.label }}>TIPO DE DESCONTO</label>
            <select
              value={discountType}
              onChange={e => setDiscountType(e.target.value as Coupon['discount_type'])}
              className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
            >
              <option value="percent">Percentual (%) sobre o preço</option>
              <option value="value">Valor fixo (R$) sobre o preço</option>
              <option value="tokens">Tokens de bônus (grátis, somados à compra)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: ui.label }}>
              VALOR ({DISCOUNT_LABELS[discountType]})
            </label>
            <input
              type="number"
              value={discountValue}
              onChange={e => setDiscountValue(Number(e.target.value))}
              className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: ui.label }}>LIMITE DE USOS (0 = ilimitado)</label>
            <input
              type="number"
              value={maxRedemptions}
              onChange={e => setMaxRedemptions(Number(e.target.value))}
              className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: ui.label }}>VÁLIDO ATÉ (opcional)</label>
            <input
              type="date"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}`, colorScheme: 'dark' }}
            />
          </div>
        </div>

        {msg && (
          <p className={`text-sm ${msg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{msg.text}</p>
        )}

        <button
          onClick={create}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)' }}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {saving ? 'Criando…' : 'Criar Cupom'}
        </button>
      </div>

      {/* Lista de cupons */}
      <div className="rounded-xl p-5" style={{ background: ui.card, border: `1px solid ${ui.border}` }}>
        <h3 className="text-sm font-semibold text-white mb-4">Cupons existentes</h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-white/40" size={24} />
          </div>
        ) : coupons.length === 0 ? (
          <p className="text-sm text-white/40">Nenhum cupom criado ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide" style={{ color: ui.label }}>
                  <th className="pb-2 pr-3">Código</th>
                  <th className="pb-2 pr-3">Desconto</th>
                  <th className="pb-2 pr-3">Usos</th>
                  <th className="pb-2 pr-3">Validade</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.id} className="border-t" style={{ borderColor: ui.border }}>
                    <td className="py-2 pr-3 font-mono text-purple-300">{c.code}</td>
                    <td className="py-2 pr-3 text-white/70">
                      {c.discount_value} {DISCOUNT_LABELS[c.discount_type]}
                    </td>
                    <td className="py-2 pr-3 text-white/50">
                      {c.redeemed_count}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}
                    </td>
                    <td className="py-2 pr-3 text-white/50">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString('pt-BR') : 'Sem prazo'}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={c.active ? 'text-green-400' : 'text-gray-500'}>
                        {c.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-3">
                        {c.active && (
                          <button
                            onClick={() => deactivate(c.id)}
                            className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                            title="Desativar cupom"
                          >
                            <Ban size={14} /> Desativar
                          </button>
                        )}
                        <button
                          onClick={() => remove(c.id)}
                          onBlur={() => setConfirmDeleteId(prev => (prev === c.id ? null : prev))}
                          className="flex items-center gap-1 text-xs hover:opacity-80"
                          style={{ color: confirmDeleteId === c.id ? '#f87171' : 'rgba(255,255,255,0.4)' }}
                          title="Apagar cupom definitivamente"
                        >
                          <Trash2 size={14} /> {confirmDeleteId === c.id ? 'Confirmar?' : 'Apagar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponsManager;
